import { motion } from "framer-motion";
import Button from "@/components/site/Button";
import useSeo from "@/hooks/useSeo";
import { breadcrumbLd } from "@/lib/jsonld";
import { usePageContent, ContentLoading } from "@/lib/content";

const EASE = [0.16, 1, 0.3, 1];

// Terugval-teksten: alleen gebruikt als de content-API faalt of te traag is.
const DEFAULTS = {
    "wat-is-human-design.seo-title": "Wat is Human Design? | Human Design Studio",
    "wat-is-human-design.seo-description":
        "Human Design in het kort: de bodygraph, de vier types en je autoriteit. Ontdek hoe het werkt en bereken gratis je bodygraph.",
    "wat-is-human-design.intro.eyebrow": "Grondbeginselen",
    "wat-is-human-design.intro.h1-a": "Wat is",
    "wat-is-human-design.intro.h1-accent": "Human Design",
    "wat-is-human-design.intro.h1-b": "?",
    "wat-is-human-design.intro.lead":
        "Human Design is geen geloof. Het is een concrete kaart van hoe jij in elkaar zit.",
    "wat-is-human-design.intro.p1":
        "Human Design combineert oude wijsheid met moderne inzichten. Astrologie, de I Tjing, het chakrasysteem en de kabbalistische levensboom, samen met kwantumfysica en genetica. Daaruit komt een praktisch stappenplan om jezelf te leren kennen.",
    "wat-is-human-design.intro.p2":
        "Het laat zien waar je sterk in bent, waar je uitdagingen liggen en hoe je keuzes maakt die echt bij je passen. Zo leef je met minder weerstand.",
    "wat-is-human-design.intro.p3":
        "Je hoeft er niets van te geloven. Zie het als een experiment. Je probeert het uit en kijkt wat het met je doet.",
    "wat-is-human-design.intro.quote":
        "Human Design is geen geloofssysteem. Het is een concrete kaart naar de aard van ons bestaan.",
    "wat-is-human-design.intro.quote-bron":
        "Ra Uru Hu, grondlegger van Human Design",
    "wat-is-human-design.bodygraph.eyebrow": "02 · De kaart",
    "wat-is-human-design.bodygraph.heading-a": "Jouw",
    "wat-is-human-design.bodygraph.heading-accent": "bodygraph",
    "wat-is-human-design.bodygraph.p1":
        "Op basis van je geboortedatum, je geboortetijd en je geboorteplaats wordt een unieke kaart berekend. Die kaart heet de bodygraph. Je kunt het vergelijken met een geboortehoroscoop, maar dan een stuk uitgebreider.",
    "wat-is-human-design.bodygraph.p2":
        "De bodygraph is het topje van de ijsberg. Het is het startpunt van je onderzoek, niet het eindpunt.",
    "wat-is-human-design.bodygraph.cta": "Bereken gratis je bodygraph",
    "wat-is-human-design.types.eyebrow": "03 · Types",
    "wat-is-human-design.types.heading-a": "De vier",
    "wat-is-human-design.types.heading-accent": "types",
    "wat-is-human-design.types.intro":
        "Uit je bodygraph komt je Type. Er zijn vier types, en elk type heeft een eigen manier om met energie en met de wereld om te gaan. Herken je jezelf?",
    "wat-is-human-design.types.pct-label": "ongeveer",
    "wat-is-human-design.types.wegwijzers-label": "Wegwijzers",
    "wat-is-human-design.types.generator.naam": "Generator",
    "wat-is-human-design.types.generator.pct": "70%",
    "wat-is-human-design.types.generator.body": [
        "De Generator is een energietype. Je bent hier om energie te leveren en werk te doen, en je hebt daar ook de energie voor. Als het het juiste werk voor jou is.",
        "Een Generator is gemaakt om te reageren. Je reageert op wat er op je pad komt en voelt via je autoriteit of het klopt. Dan heb je ook de energie om te leveren.",
        "Je aura is open en innemend. Je bent hier om jezelf te leren kennen.",
        "De Manifesterende Generator is een subtype en is ook echt een Generator.",
    ],
    "wat-is-human-design.types.generator.wegwijzers":
        "voldoening als het klopt, frustratie als het niet klopt",
    "wat-is-human-design.types.manifestor.naam": "Manifestor",
    "wat-is-human-design.types.manifestor.pct": "9%",
    "wat-is-human-design.types.manifestor.body": [
        "De Manifestor is ook een energietype. Je hebt de energie om te manifesteren en te initiëren. Je bent het enige type dat vanuit zichzelf mag beginnen, zonder eerst te reageren.",
        "Informeren is voor jou wel belangrijk. Iets starten zonder de mensen om je heen te informeren levert veel weerstand op.",
        "Je aura is gesloten en kan wat afstandelijk overkomen. Dat beschermt je. Je bent hier om impact te hebben.",
    ],
    "wat-is-human-design.types.manifestor.wegwijzers":
        "vrede als het klopt, boosheid als het niet klopt",
    "wat-is-human-design.types.projector.naam": "Projector",
    "wat-is-human-design.types.projector.pct": "20%",
    "wat-is-human-design.types.projector.body": [
        "De Projector is geen energietype. Je bent hier om te begeleiden, niet om zelf al het werk te doen.",
        "Je hebt erkenning en een uitnodiging nodig. Daarna voel je via je autoriteit of die uitnodiging past. Pas dan kun je echt begeleiden. Ga je begeleiden zonder erkenning, dan levert dat veel weerstand op.",
        "Je aura is doordringend. Je bent hier om de ander te leren kennen.",
        "Marcel is zelf een Projector.",
    ],
    "wat-is-human-design.types.projector.wegwijzers":
        "succes als het klopt, bitterheid als het niet klopt",
    "wat-is-human-design.types.reflector.naam": "Reflector",
    "wat-is-human-design.types.reflector.pct": "1%",
    "wat-is-human-design.types.reflector.body": [
        "De Reflector is geen energietype. Je bent hier om te spiegelen. In de juiste omgeving kun je vrijuit reflecteren, en daar voel je je goed bij.",
        "Voor jou werkt de autoriteit anders. Die volgt de maancyclus, dus je neemt de tijd voor een beslissing.",
        "Je spiegelt een groep of een omgeving.",
    ],
    "wat-is-human-design.types.reflector.wegwijzers":
        "verrassing als het klopt, teleurstelling als het niet klopt",
    "wat-is-human-design.autoriteit.eyebrow": "04 · Autoriteit",
    "wat-is-human-design.autoriteit.heading-a": "Hoe jij",
    "wat-is-human-design.autoriteit.heading-accent": "beslissingen",
    "wat-is-human-design.autoriteit.heading-b": "neemt",
    "wat-is-human-design.autoriteit.p1":
        "Uit je bodygraph komt ook je autoriteit. Dat is jouw manier om keuzes te maken.",
    "wat-is-human-design.autoriteit.p2":
        "De meesten van ons zijn gewend om met het hoofd te beslissen. Het hoofd is daar alleen niet zo geschikt voor. Je hoofd bekijkt een situatie van tien kanten, en daarna weet je nog steeds niet welke de juiste is.",
    "wat-is-human-design.autoriteit.p3":
        "Je autoriteit geeft je een betrouwbaardere ingang. Keuzes die beter bij je passen.",
    "wat-is-human-design.autoriteit.p4":
        "Dat is stap 1 op weg naar het leven van je eigen leven.",
    "wat-is-human-design.autoriteit.note":
        "Elk type kan een andere autoriteit hebben, behalve de Reflector. Ook kent bijna elk type verschillende subtypes.",
    "wat-is-human-design.samenwerken.eyebrow": "05 · Samenspel",
    "wat-is-human-design.samenwerken.heading-a": "Hoe types",
    "wat-is-human-design.samenwerken.heading-accent": "samenwerken",
    "wat-is-human-design.samenwerken.intro":
        "Stel je een groep van tien mensen voor die samen een project doen. Een goede verdeling ziet er zo uit: 1 Manifestor, 6 Generators, 2 Projectors en 1 Reflector.",
    "wat-is-human-design.samenwerken.loopt-label": "Als het loopt",
    "wat-is-human-design.samenwerken.loopt-p1":
        "De Manifestor start het project en informeert de anderen. De Generators reageren en leveren de energie en het werk. De Projectors zijn erkend en uitgenodigd en begeleiden het proces. De Reflector spiegelt hoe het gaat. Iedereen volgt zijn eigen autoriteit.",
    "wat-is-human-design.samenwerken.loopt-p2":
        "Het resultaat: vrede, voldoening, succes en verrassing.",
    "wat-is-human-design.samenwerken.misgaat-label": "Als het misgaat",
    "wat-is-human-design.samenwerken.misgaat-p1":
        "De Manifestor informeert niet, dus er is niets om op te reageren en de energie blijft uit. De Generators reageren op het verkeerde. De Projectors zijn niet erkend en gaan ongevraagd sturen. De Reflector zit in de verkeerde omgeving.",
    "wat-is-human-design.samenwerken.misgaat-p2":
        "Het resultaat: boosheid, frustratie, bitterheid en teleurstelling.",
    "wat-is-human-design.samenwerken.slot-p1":
        "Iedereen heeft zijn eigen rol. Er is geen goed of fout. Alleen passend of niet passend. Herkenbaar?",
    "wat-is-human-design.samenwerken.slot-p2":
        "Dit voorbeeld gaat net zo goed over een gezin, een relatie of over je eigen leven.",
    "wat-is-human-design.begin.eyebrow": "06 · Verder kijken",
    "wat-is-human-design.begin.heading-a": "Dit is nog maar het",
    "wat-is-human-design.begin.heading-accent": "begin",
    "wat-is-human-design.begin.p1":
        "Met alleen je type en je autoriteit kun je al veel helder krijgen. Samen vormen ze de eerste stap op weg naar het leven van je eigen leven.",
    "wat-is-human-design.begin.p2":
        "En toch is dit pas het topje van de ijsberg. Onder je type en je autoriteit ligt nog veel meer in de schatkist van Human Design: je profiel, je centra, kanalen en poorten. Elk stukje voegt iets toe aan het beeld van wie je bent.",
    "wat-is-human-design.cta.heading": "Benieuwd naar jouw type?",
    "wat-is-human-design.cta.text":
        "Ontdek wat je bodygraph laat zien, of bespreek het samen met Marcel in een sessie.",
    "wat-is-human-design.cta.boek": "Boek een sessie",
    "wat-is-human-design.cta.bodygraph": "Bereken gratis je bodygraph",
};

const TYPE_IDS = ["generator", "manifestor", "projector", "reflector"];

const fadeUp = {
    initial: { opacity: 0, y: 28 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-10%" },
    transition: { duration: 0.9, ease: EASE },
};

export default function WatIsHumanDesign() {
    const c = usePageContent("wat-is-human-design", DEFAULTS);
    useSeo({
        title: c
            ? c.t("wat-is-human-design.seo-title")
            : DEFAULTS["wat-is-human-design.seo-title"],
        description: c
            ? c.t("wat-is-human-design.seo-description")
            : DEFAULTS["wat-is-human-design.seo-description"],
        path: "/wat-is-human-design",
        jsonLd: [breadcrumbLd("Wat is Human Design", "/wat-is-human-design")],
    });

    if (!c) return <ContentLoading />;

    const t = (suffix) => c.t(`wat-is-human-design.${suffix}`);
    const types = TYPE_IDS.map((id) => ({
        name: t(`types.${id}.naam`),
        pct: t(`types.${id}.pct`),
        body: c.list(`wat-is-human-design.types.${id}.body`),
        wegwijzers: t(`types.${id}.wegwijzers`),
    }));

    return (
        <div data-testid="page-wat-is-human-design">
            {/* ============ SECTIE 1: INTRO ============ */}
            <section className="mx-auto max-w-[1440px] px-5 md:px-10 pt-24 md:pt-40 pb-16 md:pb-24">
                <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-clay)]/90 mb-6"
                >
                    {t("intro.eyebrow")}
                </motion.p>

                <div className="overflow-hidden">
                    <motion.h1
                        initial={{ y: "110%" }}
                        animate={{ y: "0%" }}
                        transition={{ duration: 1.05, ease: EASE, delay: 0.05 }}
                            className="font-[Fraunces] text-[13vw] md:text-[9vw] lg:text-[96px] leading-[0.98] tracking-[-0.02em] text-[var(--hds-ink)]"
                        style={{ fontWeight: 300 }}
                    >
                        {t("intro.h1-a")}{" "}
                        <em
                            className="italic text-[var(--hds-clay)]"
                            style={{ fontWeight: 300 }}
                        >
                            {t("intro.h1-accent")}
                        </em>
                        {t("intro.h1-b")}
                    </motion.h1>
                </div>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, ease: EASE, delay: 0.5 }}
                    className="mt-10 md:mt-14 max-w-[38ch] font-[Fraunces] text-[26px] md:text-[34px] leading-[1.18] tracking-[-0.01em] text-[var(--hds-ink)]"
                    style={{ fontWeight: 300 }}
                    data-testid="whd-lead"
                >
                    {t("intro.lead")}
                </motion.p>

                <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
                    <motion.div
                        {...fadeUp}
                        className="md:col-span-7 space-y-5 text-[16.5px] md:text-[17.5px] leading-[1.65] text-[var(--hds-ink)]/82 max-w-[62ch]"
                    >
                        <p>{t("intro.p1")}</p>
                        <p>{t("intro.p2")}</p>
                        <p>{t("intro.p3")}</p>
                    </motion.div>

                    <motion.figure
                        {...fadeUp}
                        transition={{
                            duration: 0.9,
                            ease: EASE,
                            delay: 0.15,
                        }}
                        className="md:col-span-5 relative"
                        data-testid="whd-quote"
                    >
                        <div className="relative rounded-[14px] bg-[var(--hds-card)] border border-[var(--hds-line)] p-8 md:p-10 shadow-[0_20px_60px_rgba(44,39,34,0.06)]">
                            <span
                                aria-hidden
                                className="absolute -top-3 left-8 font-[Fraunces] text-[80px] leading-none text-[var(--hds-clay)] select-none"
                                style={{
                                    fontWeight: 300,
                                    fontStyle: "italic",
                                }}
                            >
                                &ldquo;
                            </span>
                            <blockquote
                                className="font-[Fraunces] text-[22px] md:text-[26px] leading-[1.28] tracking-[-0.01em] text-[var(--hds-clay-dark)]"
                                style={{
                                    fontWeight: 300,
                                    fontStyle: "italic",
                                }}
                            >
                                {t("intro.quote")}
                            </blockquote>
                            <figcaption className="mt-6 flex items-center gap-3 text-[12px] tracking-[0.2em] uppercase text-[var(--hds-ink)]/55">
                                <span className="inline-block w-8 h-px bg-[var(--hds-clay)]" />
                                {t("intro.quote-bron")}
                            </figcaption>
                        </div>
                    </motion.figure>
                </div>
            </section>

            <div className="mx-auto max-w-[1440px] px-5 md:px-10">
                <div className="hds-hair" />
            </div>

            {/* ============ SECTIE 2: BODYGRAPH ============ */}
            <section className="mx-auto max-w-[1440px] px-5 md:px-10 py-20 md:py-32">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <motion.p
                            {...fadeUp}
                            className="text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-clay)] mb-6"
                        >
                            {t("bodygraph.eyebrow")}
                        </motion.p>
                        <motion.h2
                            {...fadeUp}
                            transition={{
                                duration: 1,
                                ease: EASE,
                                delay: 0.05,
                            }}
                            className="font-[Fraunces] text-[44px] md:text-[64px] leading-[1] tracking-[-0.02em]"
                            style={{ fontWeight: 300 }}
                        >
                            {t("bodygraph.heading-a")}{" "}
                            <em
                                className="italic text-[var(--hds-clay)]"
                                style={{ fontWeight: 300 }}
                            >
                                {t("bodygraph.heading-accent")}
                            </em>
                        </motion.h2>
                    </div>
                    <motion.div
                        {...fadeUp}
                        transition={{ duration: 1, ease: EASE, delay: 0.1 }}
                        className="md:col-span-7 md:pt-4 space-y-5 text-[16.5px] md:text-[17.5px] leading-[1.65] text-[var(--hds-ink)]/82 max-w-[60ch]"
                    >
                        <p>{t("bodygraph.p1")}</p>
                        <p>{t("bodygraph.p2")}</p>
                        <div className="pt-4">
                            <Button
                                href="https://www.ihdschool.com/get-your-chart"
                                data-testid="whd-bodygraph-link"
                                variant="secondary"
                                icon="external"
                            >
                                {t("bodygraph.cta")}
                            </Button>
                        </div>
                    </motion.div>
                </div>
            </section>

            <div className="mx-auto max-w-[1440px] px-5 md:px-10">
                <div className="hds-hair" />
            </div>

            {/* ============ SECTIE 3: VIER TYPES ============ */}
            <section
                className="mx-auto max-w-[1440px] px-5 md:px-10 py-20 md:py-32"
                data-testid="whd-types-section"
            >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14 md:mb-20">
                    <div className="md:col-span-5">
                        <motion.p
                            {...fadeUp}
                            className="text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-clay)] mb-6"
                        >
                            {t("types.eyebrow")}
                        </motion.p>
                        <motion.h2
                            {...fadeUp}
                            transition={{
                                duration: 1,
                                ease: EASE,
                                delay: 0.05,
                            }}
                            className="font-[Fraunces] text-[44px] md:text-[64px] leading-[1] tracking-[-0.02em]"
                            style={{ fontWeight: 300 }}
                        >
                            {t("types.heading-a")}{" "}
                            <em
                                className="italic text-[var(--hds-clay)]"
                                style={{ fontWeight: 300 }}
                            >
                                {t("types.heading-accent")}
                            </em>
                        </motion.h2>
                    </div>
                    <motion.p
                        {...fadeUp}
                        transition={{ duration: 1, ease: EASE, delay: 0.1 }}
                        className="md:col-span-7 md:pt-6 text-[17px] md:text-[18px] leading-[1.6] text-[var(--hds-ink)]/82 max-w-[58ch]"
                    >
                        {t("types.intro")}
                    </motion.p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                    {types.map((typeCard, i) => (
                        <motion.article
                            key={typeCard.name}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-8%" }}
                            transition={{
                                duration: 0.9,
                                ease: EASE,
                                delay: (i % 2) * 0.08,
                            }}
                            data-testid={`whd-type-card-${typeCard.name.toLowerCase()}`}
                            className="group relative rounded-[14px] bg-[var(--hds-card)] border border-[var(--hds-line)] p-8 md:p-10 shadow-[0_20px_60px_rgba(44,39,34,0.04)] hover:shadow-[0_28px_80px_rgba(44,39,34,0.08)] transition-shadow duration-700"
                        >
                            <div className="flex items-baseline justify-between gap-4 mb-6 pb-6 border-b border-[var(--hds-line)]">
                                <h3
                                    className="font-[Fraunces] text-[34px] md:text-[42px] leading-none tracking-[-0.01em] text-[var(--hds-ink)]"
                                    style={{ fontWeight: 300 }}
                                >
                                    {typeCard.name}
                                </h3>
                                <div
                                    data-testid={`whd-type-pct-${typeCard.name.toLowerCase()}`}
                                    className="text-right shrink-0"
                                >
                                    <span
                                        className="block text-[10px] tracking-[0.24em] uppercase text-[var(--hds-ink)]/50 mb-1"
                                        style={{ fontFamily: "inherit" }}
                                    >
                                        {t("types.pct-label")}
                                    </span>{" "}
                                    <span
                                        className="block font-[Fraunces] text-[42px] md:text-[56px] leading-none tracking-[-0.02em] text-[var(--hds-clay)]"
                                        style={{
                                            fontWeight: 300,
                                            fontStyle: "italic",
                                        }}
                                    >
                                        {typeCard.pct}
                                    </span>
                                </div>
                            </div>

                            <div className="space-y-4 text-[15.5px] md:text-[16px] leading-[1.62] text-[var(--hds-ink)]/82">
                                {typeCard.body.map((p, k) => (
                                    <p key={k}>{p}</p>
                                ))}
                            </div>

                            <div className="mt-8 pt-6 border-t border-[var(--hds-line)]">
                                <p className="text-[11.5px] tracking-[0.22em] uppercase text-[var(--hds-ink)]/50 mb-2">
                                    {t("types.wegwijzers-label")}
                                </p>
                                <p
                                    data-testid={`whd-type-wegwijzers-${typeCard.name.toLowerCase()}`}
                                    className="font-semibold text-[15px] md:text-[15.5px] leading-[1.55] text-[var(--hds-green)]"
                                >
                                    {typeCard.wegwijzers}
                                </p>
                            </div>
                        </motion.article>
                    ))}
                </div>
            </section>

            <div className="mx-auto max-w-[1440px] px-5 md:px-10">
                <div className="hds-hair" />
            </div>

            {/* ============ SECTIE 4: AUTORITEIT ============ */}
            <section className="mx-auto max-w-[1440px] px-5 md:px-10 py-20 md:py-32">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <motion.p
                            {...fadeUp}
                            className="text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-clay)] mb-6"
                        >
                            {t("autoriteit.eyebrow")}
                        </motion.p>
                        <motion.h2
                            {...fadeUp}
                            transition={{
                                duration: 1,
                                ease: EASE,
                                delay: 0.05,
                            }}
                            className="font-[Fraunces] text-[44px] md:text-[60px] leading-[1.02] tracking-[-0.02em]"
                            style={{ fontWeight: 300 }}
                        >
                            {t("autoriteit.heading-a")}{" "}
                            <em
                                className="italic text-[var(--hds-clay)]"
                                style={{ fontWeight: 300 }}
                            >
                                {t("autoriteit.heading-accent")}
                            </em>{" "}
                            {t("autoriteit.heading-b")}
                        </motion.h2>
                    </div>
                    <motion.div
                        {...fadeUp}
                        transition={{ duration: 1, ease: EASE, delay: 0.1 }}
                        className="md:col-span-7 md:pt-4"
                    >
                        <div className="space-y-5 text-[16.5px] md:text-[17.5px] leading-[1.65] text-[var(--hds-ink)]/82 max-w-[60ch]">
                            <p>{t("autoriteit.p1")}</p>
                            <p>{t("autoriteit.p2")}</p>
                            <p>{t("autoriteit.p3")}</p>
                            <p>{t("autoriteit.p4")}</p>
                        </div>
                        <p className="mt-8 text-[13.5px] leading-[1.6] text-[var(--hds-ink)]/55 max-w-[58ch] italic">
                            {t("autoriteit.note")}
                        </p>
                    </motion.div>
                </div>
            </section>

            <div className="mx-auto max-w-[1440px] px-5 md:px-10">
                <div className="hds-hair" />
            </div>

            {/* ============ SECTIE 5: SAMENWERKEN ============ */}
            <section className="mx-auto max-w-[1440px] px-5 md:px-10 py-20 md:py-32">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14 md:mb-16">
                    <div className="md:col-span-5">
                        <motion.p
                            {...fadeUp}
                            className="text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-clay)] mb-6"
                        >
                            {t("samenwerken.eyebrow")}
                        </motion.p>
                        <motion.h2
                            {...fadeUp}
                            transition={{
                                duration: 1,
                                ease: EASE,
                                delay: 0.05,
                            }}
                            className="font-[Fraunces] text-[44px] md:text-[60px] leading-[1.02] tracking-[-0.02em]"
                            style={{ fontWeight: 300 }}
                        >
                            {t("samenwerken.heading-a")}{" "}
                            <em
                                className="italic text-[var(--hds-clay)]"
                                style={{ fontWeight: 300 }}
                            >
                                {t("samenwerken.heading-accent")}
                            </em>
                        </motion.h2>
                    </div>
                    <motion.p
                        {...fadeUp}
                        transition={{ duration: 1, ease: EASE, delay: 0.1 }}
                        className="md:col-span-7 md:pt-6 text-[17px] md:text-[18px] leading-[1.6] text-[var(--hds-ink)]/82 max-w-[60ch]"
                    >
                        {t("samenwerken.intro")}
                    </motion.p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-8%" }}
                        transition={{ duration: 0.9, ease: EASE }}
                        data-testid="whd-samenwerken-loopt"
                        className="rounded-[14px] p-8 md:p-10 border border-[var(--hds-green)]/25"
                        style={{ background: "rgba(76,93,78,0.08)" }}
                    >
                        <p className="text-[11.5px] tracking-[0.24em] uppercase text-[var(--hds-green)] mb-4">
                            {t("samenwerken.loopt-label")}
                        </p>
                        <div className="space-y-4 text-[15.5px] md:text-[16px] leading-[1.65] text-[var(--hds-ink)]/85">
                            <p>{t("samenwerken.loopt-p1")}</p>
                            <p className="font-semibold text-[var(--hds-green)]">
                                {t("samenwerken.loopt-p2")}
                            </p>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-8%" }}
                        transition={{ duration: 0.9, ease: EASE, delay: 0.08 }}
                        data-testid="whd-samenwerken-misgaat"
                        className="rounded-[14px] p-8 md:p-10 border border-[var(--hds-clay)]/30"
                        style={{ background: "rgba(192,112,63,0.08)" }}
                    >
                        <p className="text-[11.5px] tracking-[0.24em] uppercase text-[var(--hds-clay-dark)] mb-4">
                            {t("samenwerken.misgaat-label")}
                        </p>
                        <div className="space-y-4 text-[15.5px] md:text-[16px] leading-[1.65] text-[var(--hds-ink)]/85">
                            <p>{t("samenwerken.misgaat-p1")}</p>
                            <p className="font-semibold text-[var(--hds-clay-dark)]">
                                {t("samenwerken.misgaat-p2")}
                            </p>
                        </div>
                    </motion.div>
                </div>

                <motion.div
                    {...fadeUp}
                    transition={{ duration: 1, ease: EASE, delay: 0.1 }}
                    className="mt-14 md:mt-20 max-w-[62ch] space-y-4 text-[16.5px] md:text-[17.5px] leading-[1.65] text-[var(--hds-ink)]/82"
                >
                    <p>{t("samenwerken.slot-p1")}</p>
                    <p>{t("samenwerken.slot-p2")}</p>
                </motion.div>
            </section>

            <div className="mx-auto max-w-[1440px] px-5 md:px-10">
                <div className="hds-hair" />
            </div>

            {/* ============ SECTIE 6: DIT IS NOG MAAR HET BEGIN ============ */}
            <section
                className="mx-auto max-w-[1440px] px-5 md:px-10 py-20 md:py-32"
                data-testid="whd-begin-section"
            >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <motion.p
                            {...fadeUp}
                            className="text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-clay)] mb-6"
                        >
                            {t("begin.eyebrow")}
                        </motion.p>
                        <motion.h2
                            {...fadeUp}
                            transition={{
                                duration: 1,
                                ease: EASE,
                                delay: 0.05,
                            }}
                            className="font-[Fraunces] text-[44px] md:text-[60px] leading-[1.02] tracking-[-0.02em]"
                            style={{ fontWeight: 300 }}
                        >
                            {t("begin.heading-a")}{" "}
                            <em
                                className="italic text-[var(--hds-clay)]"
                                style={{ fontWeight: 300 }}
                            >
                                {t("begin.heading-accent")}
                            </em>
                        </motion.h2>
                    </div>
                    <motion.div
                        {...fadeUp}
                        transition={{ duration: 1, ease: EASE, delay: 0.1 }}
                        className="md:col-span-7 md:pt-4 space-y-5 text-[16.5px] md:text-[17.5px] leading-[1.65] text-[var(--hds-ink)]/82 max-w-[60ch]"
                    >
                        <p>{t("begin.p1")}</p>
                        <p>{t("begin.p2")}</p>
                    </motion.div>
                </div>
            </section>

            {/* ============ SECTIE 7: CTA-BLOK ============ */}
            <section className="mx-auto max-w-[1440px] px-5 md:px-10 pb-24 md:pb-40">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-8%" }}
                    transition={{ duration: 0.9, ease: EASE }}
                    data-testid="whd-cta-block"
                    className="relative overflow-hidden rounded-[18px] bg-[var(--hds-clay)] text-[var(--hds-card)] px-8 md:px-16 py-14 md:py-20"
                >
                    <div
                        aria-hidden
                        className="pointer-events-none absolute -right-16 -top-16 w-[280px] h-[280px] rounded-full"
                        style={{
                            background:
                                "radial-gradient(circle at 35% 30%, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 70%)",
                        }}
                    />
                    <div className="relative max-w-[52ch]">
                        <h2
                            className="font-[Fraunces] text-[36px] md:text-[56px] leading-[1.04] tracking-[-0.02em] text-[var(--hds-card)]"
                            style={{ fontWeight: 300 }}
                        >
                            {t("cta.heading")}
                        </h2>
                        <p className="mt-6 text-[16px] md:text-[17.5px] leading-[1.6] text-[var(--hds-card)]/85 max-w-[46ch]">
                            {t("cta.text")}
                        </p>
                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            <Button
                                to="/boeken"
                                data-testid="whd-cta-boek"
                                variant="onClayPrimary"
                                icon="arrow"
                            >
                                {t("cta.boek")}
                            </Button>
                            <Button
                                href="https://www.ihdschool.com/get-your-chart"
                                data-testid="whd-cta-bodygraph"
                                variant="onClaySecondary"
                                icon="external"
                            >
                                {t("cta.bodygraph")}
                            </Button>
                        </div>
                    </div>
                </motion.div>
            </section>
        </div>
    );
}
